import cloneDeep from 'lodash/cloneDeep';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import update from 'lodash/update';
import {
  formatActionText,
  getCheckSection,
  getIsUnplannedReactiveScope,
} from 'pages/WellGuardian/WIR/utils';
import {
  ENGINEERING_WORK_ACTION_LABEL,
  FINAL_PREPARATION_ACTION_LABEL,
  INTERVENTION_CLOSE_OUT_ACTION_LABEL,
  NO,
  REACTIVE_SCOPE_ACTION_LABEL,
  STAGE_GATE_3_ACTION_LABEL,
  STAGE_GATE_5_ACTION_LABEL,
  WIREngineeringWorkSections,
  WIRFinalPreparationSections,
  WIRInterventionCloseOutSections,
  YES,
} from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  ActionsList,
  ChecklistQuestion,
  CountryData,
  WIRAction,
  WIRActionStatus,
  WIRChecklist,
} from 'types/wellGuardian';
import { useSelectUser } from '../../../../redux/User/selectors';
import { updateSelectedWIR } from '../../../../redux/WellGuardian';
import {
  useCountryState,
  useReviewChecklist,
  useSelectedWIR,
} from '../../../../redux/WellGuardian/selectors';

export const useChecklistTableData = () => {
  const dispatch = useDispatch();
  const countryState = useCountryState();
  const wirFromState = useSelectedWIR();
  const wir = useMemo(() => cloneDeep(wirFromState), [wirFromState]);
  const user = useSelectUser();
  const userName = useMemo(() => `${user?.lastName ?? ''} ${user?.firstName ?? ''}`.trim(), [user]);
  const reviewChecklist = useReviewChecklist();

  const getResponsibleName = useCallback(
    (action: string | undefined) => {
      if (reviewChecklist) {
        for (const checklist in reviewChecklist) {
          const checklistItem = reviewChecklist[checklist as keyof typeof reviewChecklist]?.find(
            item => item?.no_action === action || item?.yes_action === action,
          );
          if (checklistItem) {
            {
              /* istanbul ignore next */
            }
            return checklistItem?.check_responsible ? checklistItem?.check_responsible : '';
          }
        }
      }

      return '';
    },
    [reviewChecklist],
  );

  const getActionItemData = useCallback(
    (actionName: string) => {
      const actionItem = wir?.actions?.find(item => item.actionName === actionName);
      const actionItemPayload: WIRAction = {
        actionName,
        actionId: actionItem ? actionItem.actionId : 0,
        userActionee: actionItem ? actionItem.userActionee : '',
        actioneeName: actionItem ? actionItem.actioneeName : '',
        state: WIRActionStatus.CLOSED,
        freeText: actionItem ? actionItem.freeText : '',
        dateLastUpdated: new Date().toISOString(),
        userLastUpdated: userName,
        action: '',
        responsibleName: actionItem ? actionItem?.responsibleName : '',
      };

      return {
        actionItem,
        actionItemPayload,
      };
    },
    [userName, wir],
  );

  const getChecklistItemData = useCallback(
    (check: ChecklistQuestion, value: string, checklistType: keyof WIRChecklist) => {
      const checklist = wir?.checklist?.[checklistType] ?? [];
      const checkItem = checklist?.find(item => item.checkId === check.check_id);
      const isNotTicked = checkItem?.state === value;
      const checkItemPayload = {
        checkId: check.check_id,
        /* istanbul ignore next */
        state: isNotTicked ? '' : value,
        freeText: checkItem ? checkItem.freeText : '',
        checkSection: getCheckSection(checklistType),
        dateLastUpdated: new Date().toISOString(),
        userLastUpdated: userName,
      };

      return {
        isNotTicked,
        checkItemPayload,
        checkItem,
        checklist,
      };
    },
    [userName, wir],
  );

  const processAction = useCallback(
    (action: string, value: string, isUnticked: boolean) => {
      const updateWIRAction = (actionName: string, actionToShow: string, action: string) => {
        const { actionItem, actionItemPayload } = getActionItemData(actionName);
        if (actionToShow === value && !isUnticked) {
          actionItemPayload.state = WIRActionStatus.CLOSED;
        } else {
          actionItemPayload.state = WIRActionStatus.DELETED;
        }
        actionItemPayload.responsibleName = getResponsibleName(action);
        const actionsWithoutSelectedAction = differenceWith(wir?.actions, [actionItem], isEqual);
        wir && update(wir, `actions`, () => [...actionsWithoutSelectedAction, actionItemPayload]);
      };

      const getPhaseLabel = (label: string) => {
        const isUnplannedReactiveScope = getIsUnplannedReactiveScope(wir);

        /* istanbul ignore next */
        return isUnplannedReactiveScope ? REACTIVE_SCOPE_ACTION_LABEL : label;
      };

      switch (action) {
        case ActionsList.FINAL_PREPARATION_HSE_NOTIFICATION: {
          updateWIRAction(
            formatActionText(
              WIRFinalPreparationSections.hseNotification,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(FINAL_PREPARATION_ACTION_LABEL),
            ),
            NO,
            action,
          );
          break;
        }
        case ActionsList.FINAL_PREPARATION_WONS_APPLICATION: {
          updateWIRAction(
            formatActionText(
              WIRInterventionCloseOutSections.wonsCloseOut,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_5_ACTION_LABEL)
                : getPhaseLabel(INTERVENTION_CLOSE_OUT_ACTION_LABEL),
            ),
            YES,
            action,
          );
          updateWIRAction(
            formatActionText(
              WIRFinalPreparationSections.wonsApplication,
              getPhaseLabel(
                countryState === CountryData.denmark
                  ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                  : getPhaseLabel(FINAL_PREPARATION_ACTION_LABEL),
              ),
            ),
            YES,
            action,
          );
          break;
        }
        case ActionsList.FINAL_PREPARATION_REGULAR_PERMITS_REQUESTED: {
          updateWIRAction(
            formatActionText(
              WIRFinalPreparationSections.regularPermitsRequested,
              getPhaseLabel(FINAL_PREPARATION_ACTION_LABEL),
            ),
            YES,
            action,
          );
          break;
        }
        case ActionsList.FINAL_PREPARATION_UPDATE_CHEMICAL_PERMITS: {
          updateWIRAction(
            formatActionText(
              WIRFinalPreparationSections.updateChemicalPermits,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(FINAL_PREPARATION_ACTION_LABEL),
            ),
            NO,
            action,
          );
          break;
        }
        case ActionsList.FINAL_PREPARATION_PERFORATION_PROFORMA: {
          updateWIRAction(
            formatActionText(
              WIRFinalPreparationSections.perforationProforma,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(FINAL_PREPARATION_ACTION_LABEL),
            ),
            NO,
            action,
          );
          break;
        }
        case ActionsList.FINAL_PREPARATION_FPS_WAIVER: {
          updateWIRAction(
            formatActionText(
              WIRFinalPreparationSections.fpsWaiver,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(FINAL_PREPARATION_ACTION_LABEL),
            ),
            YES,
            action,
          );
          break;
        }
        case ActionsList.FINAL_PREPARATION_BRIDGING_DOCUMENTS: {
          updateWIRAction(
            formatActionText(
              WIRFinalPreparationSections.bridgingSimopsDocuments,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(FINAL_PREPARATION_ACTION_LABEL),
            ),
            YES,
            action,
          );
          break;
        }
        case ActionsList.FINAL_PREPARATION_VESSEL_APPLICATION: {
          updateWIRAction(
            formatActionText(
              WIRFinalPreparationSections.vesselApplication,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(FINAL_PREPARATION_ACTION_LABEL),
            ),
            YES,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_HAZOP_CARRIED_OUT: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.hazopCarriedOut,
              getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            YES,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_CHEMICAL_CHECKS: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.chemicalChecksTopsideMetallurgy,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            NO,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_CROSS_CHEMICAL_COMPATIBILITY: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.crossChemicalCompatibility,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            NO,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_CHEMICAL_CHECKS_RESERVOIR: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.chemicalChecksReservoir,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            NO,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_CHEMICAL_CHECKS_FORMATION: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.chemicalChecksFormation,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            NO,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_CHEMICAL_CHECKS_TOPSIDES: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.chemicalChecksTopsideEffluent,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            NO,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_EQUIPMENT_PROCUREMENT: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.equipmentProcurement,
              getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            YES,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_RADIOACTIVE_SOURCE_REQUIREMENT: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.radioactiveSourceRequirement,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            YES,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_STRUCTUARAL_SUPPORT: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.structuralSupport,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            YES,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_LIFTING_SUPPORT: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.liftingSupport,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            YES,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_ASSET_SIDE_ENG_ASS: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.assetSideEngAss,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            NO,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_CHEMICAL_CHECKS_FLOWBACK: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.chemicalChecksFlowBack,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            NO,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_CEFAS_REGISTRATION: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.cefasRegistration,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            NO,
            action,
          );
          break;
        }
        case ActionsList.ENGINEERING_WORK_SANDING_STUDY: {
          updateWIRAction(
            formatActionText(
              WIREngineeringWorkSections.sandingStudy,
              countryState === CountryData.denmark
                ? getPhaseLabel(STAGE_GATE_3_ACTION_LABEL)
                : getPhaseLabel(ENGINEERING_WORK_ACTION_LABEL),
            ),
            YES,
            action,
          );
          break;
        }
      }
    },
    [countryState, getActionItemData, getResponsibleName, wir],
  );

  const processCheckClick = useCallback(
    (check: ChecklistQuestion, value: string, checklistType: keyof WIRChecklist) => {
      // Update checklist
      const { isNotTicked, checkItemPayload, checkItem, checklist } = getChecklistItemData(
        check,
        value,
        checklistType,
      );

      // Edit actions
      /* istanbul ignore next */
      if (check.yes_action) processAction(check.yes_action, value, isNotTicked);
      if (check.no_action) processAction(check.no_action, value, isNotTicked);

      if (wir) {
        if (!checkItem) {
          dispatch(
            updateSelectedWIR(
              update(wir, `checklist.${checklistType}`, () => [...checklist, checkItemPayload]),
            ),
          );
        } else {
          dispatch(
            updateSelectedWIR(
              update(
                wir,
                `checklist.${checklistType}[${checklist?.indexOf(checkItem)}]`,
                () => checkItemPayload,
              ),
            ),
          );
        }
      }
    },
    [dispatch, wir, getChecklistItemData, processAction],
  );

  const switchChecksClick = useCallback(
    (
      checklistForTable: ChecklistQuestion[],
      checklistType: keyof WIRChecklist,
      option: boolean,
    ) => {
      let updatedWir = cloneDeep(wir);
      for (const checkQs of checklistForTable) {
        {
          /* istanbul ignore next */
        }
        const checklist = updatedWir?.checklist?.[checklistType] ?? [];
        const checkItem = checklist?.find(item => item.checkId === checkQs.check_id);
        const checkItemPayload = {
          checkId: checkQs.check_id,
          state: '',
          freeText: '',
          checkSection: getCheckSection(checklistType),
          dateLastUpdated: new Date().toISOString(),
          userLastUpdated: userName,
          disabledCheck: !option,
        };
        if (!checkItem) {
          updatedWir =
            updatedWir &&
            update(updatedWir, `checklist.${checklistType}`, () => [
              ...checklist,
              checkItemPayload,
            ]);
        } else {
          updatedWir =
            updatedWir &&
            update(
              updatedWir,
              `checklist.${checklistType}[${checklist?.indexOf(checkItem)}]`,
              () => checkItemPayload,
            );
        }
        // Reset actions
        if (checkQs.yes_action) processAction(checkQs.yes_action, '', true);
        if (checkQs.no_action) processAction(checkQs.no_action, '', true);
      }
      dispatch(updateSelectedWIR({ ...updatedWir, actions: wir?.actions }));
    },
    [dispatch, userName, wir, processAction],
  );

  const getChecksDisabledState = useCallback(
    (checklistType: keyof WIRChecklist) => {
      return wir?.checklist?.[checklistType]?.some(item => item.disabledCheck === true);
    },
    [wir],
  );

  return {
    processCheckClick,
    switchChecksClick,
    getChecksDisabledState,
  };
};
